<template>
    <v-row>
        <v-col cols="12" md="6">
            <div class="ai-card">
                <div class="d-flex align-center mb-4">
                    <v-avatar size="44" class="mr-3 brand-avatar">
                        <v-icon color="#0C0E12">mdi-star-four-points-outline</v-icon>
                    </v-avatar>
                    <div>
                        <div class="card-title">
                            {{ $t('ai.library_title') }}
                            <ai-beta-chip class="ml-1" />
                        </div>
                        <div class="meta">{{ $t('ai.library_subtitle') }}</div>
                    </div>
                </div>

                <div class="field-label mb-1">{{ $t('ai.library_want_to_create') }}</div>
                <kimsa-select :items="typeItems" item-text="name" cols="6" :height="56" withIcon
                    color="accent" :value="type" @change="type = $event" />

                <div class="field-label mt-4 mb-1">{{ $t('ai.library_describe') }}</div>
                <kimsa-textarea-field outlined rows="5" force-value :value="prompt" @change="onPromptChange"
                    :placeholder="$t('ai.library_describe_placeholder')" />
                <div class="d-flex justify-space-between meta mt-1">
                    <span>{{ prompt.length }} / {{ maxLength }}</span>
                    <span class="clear-link" @click="clearPrompt">{{ $t('common.clean') }}</span>
                </div>

                <div class="field-label mt-4 mb-1">{{ $t('ai.library_quick_suggestions') }}</div>
                <v-chip v-for="suggestion in suggestions" :key="suggestion" class="suggestion-chip mr-2 mb-2" label small
                    :disabled="usedSuggestions.includes(suggestion)" @click="appendSuggestion(suggestion)">
                    {{ suggestion }}
                </v-chip>

                <v-row class="mt-2" dense>
                    <v-col cols="12" md="4">
                        <kimsa-combobox :label="$t('ai.library_level')" :items="levelItems" item-text="name"
                            outlined dense noObject :value="level" @change="level = $event" />
                    </v-col>
                    <v-col cols="12" md="4">
                        <kimsa-combobox :label="$t('ai.library_duration')" :items="durationItems" item-text="name"
                            outlined dense noObject :value="duration" @change="duration = $event" />
                    </v-col>
                    <v-col cols="12" md="4">
                        <kimsa-combobox :label="$t('ai.library_equipment')" :items="equipmentItems" item-text="name"
                            outlined dense noObject clearable :value="equipmentValue" @change="equipmentValue = $event" />
                    </v-col>
                </v-row>

                <v-btn block depressed color="primary" class="mt-4 black--text" :loading="generating"
                    :disabled="!prompt.trim() || usageExhausted" @click="generate">
                    <v-icon left small color="#0C0E12">mdi-star-four-points-outline</v-icon>
                    {{ $t('ai.library_generate') }}
                </v-btn>
                <div v-if="usage" class="meta text-center mt-2">
                    {{ usageExhausted
                        ? $t('ai.library_usage_exhausted')
                        : $t('ai.library_usage_remaining', { remaining: usage.remaining, limit: usage.limit }) }}
                </div>
            </div>
        </v-col>

        <v-col cols="12" md="6">
            <div class="ai-card fill-height">
                <div v-if="!result && !generating" class="empty-state">
                    <v-icon size="48" class="empty-icon">mdi-star-four-points-outline</v-icon>
                    <div class="card-title mt-3">{{ $t('ai.library_empty_title') }}</div>
                    <div class="empty-text">{{ $t('ai.library_empty_subtitle') }}</div>
                </div>

                <div v-else-if="generating" class="empty-state">
                    <v-progress-circular indeterminate color="primary" size="36" width="3" />
                </div>

                <div v-else>
                    <ai-beta-note class="mb-3" />
                    <kimsa-text-field :label="$t('common.name')" outlined dense force-value
                        :value="result.name" @change="result.name = $event" />

                    <div class="mt-3">
                        <routine-blocks :key="resultKey" force-cols12 :single-block="type === 'block'"
                            :default-blocks="editableBlocks" @change="editedBlocks = $event" />
                    </div>

                    <kimsa-combobox v-if="type === 'block'" class="mt-3" :label="$t('common.folder')"
                        :items="folderOptions" item-text="label" item-value="id" @change="folder = $event"
                        :value="folder" />

                    <v-row class="mt-3" dense>
                        <v-col cols="6">
                            <v-btn block depressed class="secondary-btn" :disabled="generating || usageExhausted" @click="generate">
                                {{ $t('ai.library_regenerate') }}
                            </v-btn>
                        </v-col>
                        <v-col cols="6">
                            <v-btn block depressed color="primary" class="black--text" :loading="saving" @click="save">
                                {{ $t('ai.library_save') }}
                            </v-btn>
                        </v-col>
                    </v-row>
                </div>
            </div>
        </v-col>
    </v-row>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import KimsaSelect from '@/components/common/inputs/KimsaSelect'
import KimsaTextareaField from '@/components/common/inputs/KimsaTextareaField'
import KimsaTextField from '@/components/common/inputs/KimsaTextField'
import KimsaCombobox from '@/components/common/inputs/KimsaCombobox'
import RoutineBlocks from '@/components/coach/routines/RoutineBlocks'
import AiBetaChip from '@/components/coach/ai/AiBetaChip'
import AiBetaNote from '@/components/coach/ai/AiBetaNote'
import { generateLibraryItem, getLibraryUsage, resolveGeneratedExercises, saveEditedBlock, saveEditedRoutine } from '@/utils/AiLibrary'
import { searchExercises, invalidateExerciseCache } from '@/utils/ExerciseLibrary'
import { equipmentLabel } from '@/utils/Taxonomy'

export default {
    name: 'AiLibraryTab',
    components: { KimsaSelect, KimsaTextareaField, KimsaTextField, KimsaCombobox, RoutineBlocks, AiBetaChip, AiBetaNote },
    data() {
        return {
            type: 'block',
            prompt: '',
            maxLength: 500,
            level: 'intermediate',
            duration: '30',
            equipmentValue: null,
            generating: false,
            saving: false,
            result: null,
            catalog: [],
            folder: null,
            usedSuggestions: [],
            editableBlocks: [],
            editedBlocks: [],
            resultKey: 0,
            usage: null,
        }
    },
    computed: {
        ...mapState(['currentUser', 'folders', 'globalFolders', 'libraryCoach', 'equipment']),
        isCompany(vm = this) {
            return vm.accountType === 'company'
        },
        coachId(vm = this) {
            if (vm.isCompany) return vm.libraryCoach?.id || null
            return vm.currentUser.uid
        },
        typeItems(vm = this) {
            return [
                { name: vm.$tc('common.blocks', 1), value: 'block', icon: 'mdi-view-grid-outline', size_icon: 28 },
                { name: vm.$t('common.routines'), value: 'routine', icon: 'mdi-format-list-bulleted', size_icon: 28 },
            ]
        },
        levelItems(vm = this) {
            return [
                { name: vm.$t('levels.beginner'), value: 'beginner' },
                { name: vm.$t('levels.intermediate'), value: 'intermediate' },
                { name: vm.$t('levels.advanced'), value: 'advanced' },
            ]
        },
        durationItems() {
            return [15, 30, 45, 60].map(m => ({ name: `${m} min`, value: String(m) }))
        },
        equipmentItems(vm = this) {
            return (vm.equipment || []).map(e => ({ name: equipmentLabel(e.name), value: e.name }))
        },
        suggestions(vm = this) {
            return [
                vm.$t('ai.library_suggestion_1'),
                vm.$t('ai.library_suggestion_2'),
                vm.$t('ai.library_suggestion_3'),
                vm.$t('ai.library_suggestion_4'),
                vm.$t('ai.library_suggestion_5'),
                vm.$t('ai.library_suggestion_6'),
            ]
        },
        usageExhausted(vm = this) {
            return !!vm.usage && vm.usage.remaining <= 0
        },
        folderOptions(vm = this) {
            return [...vm.globalFolders, ...vm.folders]
                .filter(folder => folder.type === 'blocks')
                .map(folder => ({ ...folder, id: folder.id, label: vm.folderLabel(folder) }))
        },
    },
    async mounted() {
        const vm = this
        if (vm.folders.length === 0) await vm.bindFolders(vm.coachId)
        if (vm.globalFolders.length === 0) await vm.bindGlobalFolders()
        if (vm.equipment.length === 0) await vm.bindEquipment()
        invalidateExerciseCache(vm.coachId)
        vm.catalog = await searchExercises({ coachId: vm.coachId, limit: 1000 })
        vm.usage = await getLibraryUsage().catch(() => null)
    },
    methods: {
        ...mapActions(['bindFolders', 'bindGlobalFolders', 'bindEquipment']),
        onPromptChange(value) {
            this.prompt = (value || '').slice(0, this.maxLength)
        },
        appendSuggestion(suggestion) {
            const vm = this
            if (vm.usedSuggestions.includes(suggestion)) return
            const trimmed = vm.prompt.trim()
            vm.prompt = (trimmed ? `${trimmed}. ${suggestion}` : suggestion).slice(0, vm.maxLength)
            vm.usedSuggestions.push(suggestion)
        },
        clearPrompt() {
            this.prompt = ''
            this.usedSuggestions = []
        },
        async generate() {
            const vm = this
            if (!vm.prompt.trim()) return
            vm.generating = true
            vm.result = null
            try {
                const response = await generateLibraryItem({
                    type: vm.type,
                    prompt: vm.prompt,
                    level: vm.level,
                    duration: Number(vm.duration),
                    equipments: vm.equipmentValue ? [vm.equipmentValue] : [],
                    locale: vm.$i18n.locale,
                })
                vm.result = response.result
                vm.usage = response.usage || vm.usage
                vm.editableBlocks = vm.type === 'block'
                    ? [{ exercises: resolveGeneratedExercises(vm.result.exercises, vm.catalog), isCircuit: false, type: null }]
                    : vm.result.blocks.map(block => ({
                        name: block.name || null,
                        isCircuit: false,
                        type: null,
                        exercises: resolveGeneratedExercises(block.exercises, vm.catalog),
                    }))
                vm.editedBlocks = vm.editableBlocks
                vm.resultKey++
            } catch (e) {
                vm.toast(e?.message || vm.$t('common.an_error_has_occurred'), 'error')
            } finally {
                vm.generating = false
            }
        },
        async save() {
            const vm = this
            vm.saving = true
            try {
                if (vm.type === 'block') {
                    const blockId = await saveEditedBlock(vm.coachId, vm.folder, vm.result.name, vm.editedBlocks[0])
                    vm.toast(vm.$t('common.saved_block'))
                    vm.$router.push({ name: `${vm.accountType}.blocks.edit`, params: { uid: blockId } })
                } else {
                    const routineId = await saveEditedRoutine(vm.coachId, vm.result.name, vm.editedBlocks, null)
                    vm.toast(vm.$t('routines.saved'))
                    vm.$router.push({ name: `${vm.accountType}.routines.edit`, params: { uid: routineId } })
                }
            } catch (e) {
                vm.toast(e?.message || vm.$t('common.an_error_has_occurred'), 'error')
            } finally {
                vm.saving = false
            }
        },
        refresh() { },
        setSearch() { },
    },
    watch: {
        generating(val) {
            this.$emit('loading', val)
        },
    },
}
</script>

<style scoped>
.ai-card {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    padding: 14px 18px;
    overflow: hidden;
}

.brand-avatar {
    background: var(--brand-gradient);
}

.card-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.meta {
    font-size: 11px;
    color: var(--v-txtSecondary-base);
}

.field-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
}

.clear-link {
    cursor: pointer;
    color: var(--v-txtSecondary-base);
}

.clear-link:hover {
    color: var(--v-accent-base);
}

.suggestion-chip {
    background: var(--v-surface2-base) !important;
    color: var(--v-txtSecondary-base) !important;
    border-radius: 6px !important;
    font-size: 11px;
}

.suggestion-chip:hover:not(.v-chip--disabled) {
    background: var(--v-primary-base) !important;
    color: #0C0E12 !important;
}

.secondary-btn {
    background: var(--v-surface2-base) !important;
    color: var(--v-txtSecondary-base) !important;
}

.empty-state {
    text-align: center;
    padding: 24px 0;
}

.empty-icon {
    color: var(--v-txtMuted-base) !important;
}

.empty-text {
    font-size: 13px;
    color: var(--v-txtMuted-base);
}
</style>
